// The dashboard and its route handlers are deployed as one Next.js app. In
// production, always use the current origin so a stale/invalid public API
// hostname cannot make browser requests fail before they reach Next.js.
// Keep the optional absolute URL for local development and isolated smoke
// environments only.
export const API_BASE_URL =
  process.env.NODE_ENV === "production" ? "" : process.env.NEXT_PUBLIC_API_URL ?? "";

export type ApiErrorPayload = {
  message?: string;
  error?: string;
};

export class ApiError extends Error {
  status: number;
  payload: ApiErrorPayload | null;

  constructor(status: number, payload: ApiErrorPayload | null) {
    super(payload?.message ?? payload?.error ?? "Ha ocurrido un error");
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export function getToken() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage.getItem("vilatio_token");
}

type ApiOptions = RequestInit & {
  token?: string | null;
};

export async function apiFetch<T>(path: string, options: ApiOptions = {}) {
  const { token = getToken(), headers, body, ...rest } = options;
  const requestHeaders = new Headers(headers);

  if (body && !(body instanceof FormData) && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", "application/json");
  }

  if (token) {
    requestHeaders.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    body,
    headers: requestHeaders,
  });

  const contentType = response.headers.get("content-type");
  const hasJson = contentType?.includes("application/json");
  const payload = hasJson ? await response.json() : null;

  if (!response.ok) {
    throw new ApiError(response.status, payload);
  }

  return payload as T;
}

export function jsonBody<T>(body: T) {
  return JSON.stringify(body);
}

export function unwrapArray<T>(payload: unknown, keys: string[] = []): T[] {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const candidates = [...keys, "data", "items", "results"];

    for (const key of candidates) {
      if (Array.isArray(record[key])) {
        return record[key] as T[];
      }
    }
  }

  return [];
}

export function unwrapObject<T>(payload: unknown, keys: string[] = []): T {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;

    for (const key of keys) {
      const value = record[key];
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return value as T;
      }
    }
  }

  return payload as T;
}

/** Returns an optimised upload URL. displayPx is the CSS display width; we request 2× for retina. */
export function imgUrl(filename: string, displayPx: number): string {
  return `/api/uploads/${filename}?w=${displayPx * 2}`;
}
